import { Injectable, inject } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { TaxIdValidity, TaxService } from '@fmnts/common/tax';
import { hasProperty, isRecord } from '@fmnts/core';
import { coerceIsoDate, isValidDate } from '@fmnts/core/chronos';
import {
  FormInputField,
  IValidationService,
  ValidatorConfig,
} from '@fmnts/declarative-forms/core';

export enum TaxValidatorName {
  TaxIdNumber = 'taxIdentificationNumber',
}

declare interface TaxIdValidationArgs {
  /**
   * Determines how to handle cases in which the Tax ID validity is "unknown"
   * due to missing region information.
   */
  unknownValidityHandling?: 'valid' | 'invalid';
}

interface TaxIdValidationError {
  taxId: string;
  reason: TaxIdValidity;
  /** ISO region */
  region: string;
}

export const hasTaxIdError = hasProperty(
  'taxId',
  (u): u is TaxIdValidationError => isRecord(u),
);

/**
 * Service for building validators for tax related validations.
 */
@Injectable()
export class TaxValidationBuilder implements IValidationService {
  private readonly _taxService = inject(TaxService);

  buildValidator(
    validator: ValidatorConfig<unknown>,
    field: FormInputField,
  ): ValidatorFn[] {
    switch (validator.name) {
      case TaxValidatorName.TaxIdNumber:
        return this._buildTaxNumberValidator(
          validator as ValidatorConfig<TaxIdValidationArgs | undefined>,
          field.attrs?.country as string,
        );
    }

    return [];
  }

  private _buildTaxNumberValidator(
    config: ValidatorConfig<TaxIdValidationArgs | undefined>,
    region: string,
  ): ValidatorFn[] {
    // Determine validator default parameters
    const { unknownValidityHandling = 'valid' } = config.args ?? {};

    const validate = (taxId: string, properties: Record<string, unknown>) =>
      this._taxService.validate(region, taxId, properties);

    const getAdditionalData = (
      ctrl: AbstractControl,
    ): Record<string, unknown> => {
      const { root } = ctrl;
      const birthday = coerceIsoDate(
        // TODO: Remove hardcoded values to get date_of_birth field
        root.get(['personalDataKeys', 'date_of_birth'] as const)?.value,
      );

      return {
        firstname:
          root.get(['personalDataKeys', 'first_name'] as const)?.value ??
          undefined,
        lastname:
          root.get(['personalDataKeys', 'last_name'] as const)?.value ??
          undefined,
        birthday: isValidDate(birthday) ? birthday : undefined,
      };
    };

    return [
      (ctrl: AbstractControl<string>) => {
        const { value } = ctrl;
        const result = validate(value, getAdditionalData(ctrl));

        // Everythings ok
        if (result === TaxIdValidity.Valid) {
          return null;
        }

        // No validation could be applied
        if (result === TaxIdValidity.Unknown) {
          if (unknownValidityHandling === 'valid') {
            return null;
          }
        }

        return {
          taxId: {
            taxId: value,
            reason: result,
            region,
          },
        };
      },
    ];
  }
}
